/**
 * Shared row fill (background color) logic for Market Summary tables.
 * Used by both PDFReportRenderer (preview/export) and MarketTable (Live View)
 * to ensure consistent row highlighting based on aircraft status.
 *
 * ROW BACKGROUND PRECEDENCE:
 * 1. Sold section: always blue (ij-row-sold)
 * 2. Removed section: always orange (ij-row-removed)
 * 3. For-sale section:
 *    - Off-market + Deal Pending: gradient (ij-row-off-market-pending)
 *    - Off-market: green (ij-row-off-market)
 *    - Deal Pending: yellow (ij-row-deal-pending)
 *    - Default: no fill
 */

export type TableType = 'for-sale' | 'sold' | 'removed'

export interface RowFillContext {
  tableType: TableType
  isOffMarket: boolean
  isDealPending: boolean
}

/**
 * PDF-scoped row fill class names (used with .ij-report-root).
 * These classes are defined in pdf-report-legacy.css and are scoped to PDF rendering.
 */
export function getPdfRowFillClass(context: RowFillContext): string {
  const { tableType, isOffMarket, isDealPending } = context

  // Section-based colors take precedence
  if (tableType === 'sold') return 'ij-row-sold'
  if (tableType === 'removed') return 'ij-row-removed'

  // For-sale section uses status-based colors
  if (isOffMarket && isDealPending) return 'ij-row-off-market-pending'
  if (isOffMarket) return 'ij-row-off-market'
  if (isDealPending) return 'ij-row-deal-pending'

  return ''
}

/**
 * Live View row fill class names (Tailwind-based, works outside .ij-report-root).
 * These use Tailwind utility classes to match the PDF colors.
 *
 * Color reference (from pdf-report-legacy.css):
 * - Sold: #A4C4EC (blue)
 * - Removed: #F4A460 (sandybrown/orange)
 * - Off-market: #A8DBA8 (light green)
 * - Deal pending: #EDE574 (yellow)
 */
export function getLiveViewRowFillClass(context: RowFillContext): string {
  const { tableType, isOffMarket, isDealPending } = context

  // Section-based colors take precedence
  // Sold and Removed sections now use shared utility classes
  if (tableType === 'sold') return 'ij-lv-row-sold'
  if (tableType === 'removed') return 'ij-lv-row-removed'

  // For-sale section uses status-based colors
  if (isOffMarket && isDealPending) return 'ij-lv-row-off-market-pending'
  if (isOffMarket) return 'ij-lv-row-off-market hover:bg-green-50'
  if (isDealPending) return 'ij-lv-row-deal-pending'

  return 'hover:bg-gray-50'
}

/**
 * Get row fill class based on rendering context.
 * @param context - Row fill context with table type and status flags
 * @param isPdfContext - True for PDF rendering, false for Live View
 */
export function getRowFillClass(context: RowFillContext, isPdfContext: boolean): string {
  return isPdfContext ? getPdfRowFillClass(context) : getLiveViewRowFillClass(context)
}

